import { Field, Placeholder, Text, Item, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';
import type { ComponentProps } from 'lib/component-props';
import withStyles from 'lib/enhancers/withStyles';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import Select, { type SelectOption } from 'components/helpers/Select/Select';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Icon from 'components/helpers/Icon';
import styles from './PlanSearch.module.scss';
import { getCounties } from './API';
import { County } from 'models/plans-search-types';
import TextInput from 'components/helpers/TextInput/TextInput';
import { buildURL } from 'components/PlanData/utilities';
import { getCleanID } from 'lib/utils';
import { useI18n } from 'next-localization';

type PlanSearchFields = {
  counties: string[];
  heading: Field<string>;
  fieldsRequiredLabel: Field<string>;
  disclaimerText: Field<string>;
  planType: Item;
  submitLabel: Field<string>;
  zipcodeLabel: Field<string>;
  zipcodeErrorMessage: Field<string>;
  zipcodeInvalidErrorMessage: Field<string>;
  countyLabel: Field<string>;
  countyErrorMessage: Field<string>;
  currentMemeberLabel: Field<string>;
  yearLabel: Field<string>;
  planYears: string;
  initialYearSelection: number;
};

type PlanTypeFields = {
  name: Field<string>;
  planOptionsListingPage: Item;
  planSearchPage: Item;
  planDetailPage: Item;
};

type PlanSearchProps = ComponentProps & {
  fields: PlanSearchFields;
};

const cx = classNames.bind(styles);

const PlanSearch = (props: PlanSearchProps): JSX.Element => {
  const {
    heading,
    fieldsRequiredLabel,
    disclaimerText,
    planType,
    submitLabel,
    zipcodeLabel,
    zipcodeErrorMessage,
    zipcodeInvalidErrorMessage,
    countyLabel,
    countyErrorMessage,
    currentMemeberLabel,
    yearLabel,
    planYears,
    initialYearSelection,
  } = props.fields;

  const { rendering } = props;
  const { t } = useI18n();

  const [counties, setCounties] = React.useState<County[]>([]);
  const [enteredZipcode, setZipcode] = React.useState<string>('');
  const [selectedCounty, setSelectedCounty] = React.useState<number>(-1);
  const [errorMessage, setErrorMessage] = React.useState<Field<string>>();
  const [showError, setShowError] = React.useState<boolean>(false);
  const [existingCustomer, setExistingCustomer] = React.useState<boolean>(false);
  const [countyOptions, setCountyOptions] = React.useState<SelectOption[]>([]);
  const [selectedYear, setSelectedYear] = React.useState(initialYearSelection.toString());
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing, language } = sitecoreContext;

  const years = planYears.split(',');
  const typeFields = planType?.fields as PlanTypeFields;
  const headingTokenRelaced = heading?.value?.replaceAll('{type}', typeFields.name?.value || '');

  const checkZip: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const zip = e.target.value;
    setZipcode(zip);
    //Zipcode is a 5 digit number
    if (zip.length == 5) {
      setShowError(false);
      const tempCounties = await getCounties(zip, planType.id ?? '');
      if (tempCounties.length == 0) {
        setCounties([]);
        setErrorMessage(countyErrorMessage);
        setShowError(true);
      } else if (tempCounties.length == 1) {
        setSelectedCounty(0);
        setCounties(tempCounties);
        checkPlans(tempCounties[0], selectedYear);
      } else {
        setSelectedCounty(-1);
        setCounties(tempCounties);
        const options: SelectOption[] = [];

        for (let x = 0; x < tempCounties.length; x++) {
          const option: SelectOption = {
            label: { value: tempCounties[x].CountyName },
            value: x.toString(),
          };
          options.push(option);
        }
        setCountyOptions(options);
      }
    } else {
      setShowError(false);
      setErrorMessage(zipcodeInvalidErrorMessage);
      setCountyOptions([]);
      setSelectedCounty(-1);
    }
  };

  const checkPlans = (county: County, year: string) => {
    setShowError(false);
    const keys = Object.keys(county.PlansByYear);

    if (keys.length == 0 || keys.indexOf(year) == -1) {
      setErrorMessage(countyErrorMessage);
      setShowError(true);
    }
  };

  const changeExistingCustomer = (cust: boolean) => {
    setExistingCustomer(cust);
  };

  const changeYear: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    setSelectedYear(e.target.value);
    if (counties.length > 0 && selectedCounty > -1) {
      checkPlans(counties[selectedCounty], e.target.value);
    }
  };

  const changeCounty: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const num = Number.parseInt(e.target.value) ?? -1;
    setSelectedCounty(num);
    setErrorMessage(zipcodeErrorMessage);
    setShowError(num < 0);
    checkPlans(counties[num], selectedYear);
  };

  const SearchAndContinue: React.MouseEventHandler = () => {
    if (counties.length == 0 || selectedCounty < 0) {
      setShowError(true);
      if (enteredZipcode === '' || enteredZipcode.length < 5) {
        setErrorMessage(zipcodeInvalidErrorMessage);
      } else {
        setErrorMessage(countyErrorMessage);
      }
      return;
    }
    Object.entries(counties[selectedCounty].PlansByYear).forEach(([key, value]) => {
      if (key == selectedYear) {
        const tempQueries: [string, string][] = [
          ['state', value.State],
          ['county', value.County],
          ['year', value.Year],
          ['zipcode', enteredZipcode],
          ['ExistingMember', existingCustomer.toString()],
        ];
        let url = '';
        if (value.NumPlans == 1) {
          url = typeFields.planDetailPage.url ?? '';
          tempQueries.push(['planId', getCleanID(value.PlanID)]);
        } else {
          url = typeFields.planOptionsListingPage.url ?? '';
        }
        window.location.href = buildURL(url, null, tempQueries, language);
        return;
      }
    });
  };

  return (
    <section className={cx('plan-search-wrapper')}>
      <div className={cx('plan-search')}>
        <div>
          {pageEditing ? (
            <Text tag="h1" className="font-style-xl" encode={sitecoreContext.pageEditing} field={heading} />
          ) : (
            <h1 className="font-style-xl" dangerouslySetInnerHTML={{ __html: headingTokenRelaced }} />
          )}
          <Text tag="p" encode={sitecoreContext.pageEditing} field={fieldsRequiredLabel} />
        </div>
        <div className={cx('entryBoxes')}>
          <div className={cx('entryHalf')}>
            <Text field={zipcodeLabel} htmlFor="zipcode" encode={pageEditing} tag="label" />
            <br />
            <TextInput
              value={enteredZipcode}
              maxLength={5}
              type="number"
              id="zipcode"
              pattern="^[0-9]{5}$"
              placeholder={t('Zip Code')}
              onChange={checkZip}
              className={cx('inputBox')}
            />
            {showError && (
              <div role="alert" className={cx('error-msg')}>
                <Icon variant="error" />
                <Text htmlFor="zipcode" field={errorMessage} encode={pageEditing} />
              </div>
            )}
          </div>

          {countyOptions.length > 1 && (
            <div className={cx('entryHalf')}>
              <Select
                id="county"
                options={countyOptions}
                onChange={changeCounty}
                className={cx('inputBox')}
                label={countyLabel.value}
              />
            </div>
          )}
        </div>
        <div className={cx('additionalQuestions')}>
          <Text field={currentMemeberLabel} encode={pageEditing} htmlFor="isExistingCustomer" tag="label" />
          <input
            type="radio"
            name="existingCustomer"
            title="Yes"
            id="isExistingCustomer"
            checked={existingCustomer}
            onChange={() => {
              changeExistingCustomer(true);
            }}
          />
          <label htmlFor="isExistingCustomer">{t('Yes')}</label>
          <input
            type="radio"
            name="existingCustomer"
            title="No"
            id="notExistingCustomer"
            checked={!existingCustomer}
            onChange={() => {
              changeExistingCustomer(false);
            }}
          />
          <label htmlFor="notExistingCustomer">{t('No')}</label>
        </div>
        {years.length > 1 && (
          <div className={cx('additionalQuestions')}>
            <Text tag="label" field={yearLabel} htmlFor={'planYear' + new Date().getFullYear()} />
            {years.map((useYear, i) => {
              const newId = 'planYear' + useYear;
              return (
                <span key={i}>
                  <input
                    type="radio"
                    name="planYear"
                    title={useYear}
                    value={useYear}
                    id={newId}
                    onChange={changeYear}
                    checked={useYear == selectedYear}
                  />
                  <label htmlFor={newId}>{useYear}</label>
                </span>
              );
            })}
          </div>
        )}
        <p>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            className={cx('loginButton')}
            onClick={SearchAndContinue}
            disabled={pageEditing}
          >
            <Text field={submitLabel} />
          </Button>
        </p>
        <RichTextWrapper className={cx('plan-search__disclaimer')} field={disclaimerText} />
      </div>
      <aside className={cx('plan-search-listing__aside')}>
        <Placeholder key={0} name="hp-container-3070-right" rendering={rendering} />
      </aside>
    </section>
  );
};

export default withStyles()<PlanSearchProps>(PlanSearch);
